import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { generatePath, useNavigate } from 'react-router-dom'
import { ViewType } from '../assets/constants'
import ROUTES from '../assets/routes'
import { exchangeToJSON } from '../proto/generated/common'
import { Security } from '../proto/generated/security'
import { setFinancialPanelOpen, setFinancialPanelSection, setSeasonalsPanelOpen, setTechnicalsPanelOpen } from '../reducer/home.reducer'
import { updatePrimarySecurityAsync } from '../reducer/home.thunks'
import { setScalperState } from '../reducer/scalper.reducer'
import { getSecurityKey, isFnOSecurity } from '../util/security'
import { AppDispatch } from '../store'
import commonActions from '../actions/common.actions'
import { WidgetListItems } from '../util/sidebar'
import store from '../store'
import heatmapActions from '../actions/heatmap.actions'
import {
    heatmapIndexCandidatesFromSecurity,
    resolveHeatmapIndex,
    selectHeatmapStore,
} from '../components/heatmap/heatmapUtils'

const useSecurityViewSelect = ({
    security,
    expressionMode = false,
    onComplete,
}: {
    security: Security | null | undefined
    expressionMode?: boolean
    onComplete?: () => void
}) => {
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()

    const getNavRoute = useCallback(
        (route: string) => {
            const currentSecurity = expressionMode ? null : security
            const currentExchange = currentSecurity ? exchangeToJSON(currentSecurity.exchange) : 'NSE'
            const currentSymbol = currentSecurity?.code || 'NIFTY_50'

            if (route === ROUTES.optionChain) {
                return generatePath(ROUTES.optionChain, {
                    exchange: currentExchange,
                    symbol: currentSymbol,
                })
            }
            if (route === ROUTES.oiOverview) {
                return generatePath(ROUTES.oiOverview, {
                    exchange: currentExchange,
                    symbol: currentSymbol,
                })
            }
            if (route === ROUTES.openInterest) {
                return generatePath(ROUTES.openInterest, {
                    exchange: currentExchange,
                    symbol: currentSymbol,
                })
            }
            if (route === ROUTES.openInterestChange) {
                return generatePath(ROUTES.openInterestChange, {
                    exchange: currentExchange,
                    symbol: currentSymbol,
                })
            }
            if (route === ROUTES.multiStrikeChart) {
                return generatePath(ROUTES.multiStrikeChart, {
                    exchange: currentExchange,
                    symbol: currentSymbol,
                })
            }
            if (route === ROUTES.simulator) {
                return generatePath(ROUTES.simulator, {
                    exchange: currentExchange,
                    symbol: currentSymbol,
                })
            }
            if (route === ROUTES.strategyBuilder) {
                return generatePath(ROUTES.strategyBuilder, {
                    exchange: currentExchange,
                    symbol: currentSymbol,
                })
            }
            if (route === ROUTES.seasonals) {
                return generatePath(ROUTES.seasonals, {
                    exchange: currentExchange,
                    symbol: currentSymbol,
                })
            }
            if (route === ROUTES.technicals) {
                return generatePath(ROUTES.technicals, {
                    exchange: currentExchange,
                    symbol: currentSymbol,
                })
            }
            if (route === ROUTES.heatmap) {
                return ROUTES.heatmap
            }
            if (route === ROUTES.financials) {
                return generatePath(ROUTES.financials, {
                    exchange: currentExchange,
                    symbol: currentSymbol,
                })
            }
            return generatePath(route, { layoutId: '' })
        },
        [expressionMode, security],
    )

    return useCallback(
        (selectedView: ViewType) => {
            if (selectedView === ViewType.HEATMAP) {
                if (security && !expressionMode) {
                    const supportedIndices = selectHeatmapStore(store.getState())?.config?.supportedIndices ?? []
                    const resolved = resolveHeatmapIndex(
                        heatmapIndexCandidatesFromSecurity(security),
                        supportedIndices,
                    )
                    const indexHint =
                        resolved ||
                        security.symbol ||
                        security.code?.replace(/_/g, '') ||
                        security.code ||
                        ''
                    if (indexHint) {
                        heatmapActions.setIndex(dispatch, indexHint)
                    }
                }
                dispatch(setFinancialPanelOpen(false))
                dispatch(setSeasonalsPanelOpen(false))
                dispatch(setTechnicalsPanelOpen(false))
                navigate(ROUTES.heatmap)
                onComplete?.()
                return
            }

            if (selectedView === ViewType.SEASONALS || selectedView === ViewType.TECHNICALS) {
                if (security && !expressionMode) {
                    const securityKey = getSecurityKey(security.exchange, security.code)
                    const isFnO = isFnOSecurity(security)
                    updatePrimarySecurityAsync({
                        key: securityKey,
                        expressionMode: false,
                        securities: [securityKey],
                        underlyingSecurityCode: getSecurityKey(
                            security.exchange,
                            isFnO ? security.underlyingSecurityCode! : security.code,
                        ),
                    })(dispatch)
                }
                dispatch(setFinancialPanelOpen(false))
                dispatch(setSeasonalsPanelOpen(false))
                dispatch(setTechnicalsPanelOpen(false))
                navigate(
                    getNavRoute(
                        selectedView === ViewType.SEASONALS ? ROUTES.seasonals : ROUTES.technicals,
                    ),
                )
                onComplete?.()
                return
            }

            if (selectedView === ViewType.FINANCIAL_DATA) {
                if (security && !expressionMode) {
                    const securityKey = getSecurityKey(security.exchange, security.code)
                    const isFnO = isFnOSecurity(security)
                    updatePrimarySecurityAsync({
                        key: securityKey,
                        expressionMode: false,
                        securities: [securityKey],
                        underlyingSecurityCode: getSecurityKey(
                            security.exchange,
                            isFnO ? security.underlyingSecurityCode! : security.code,
                        ),
                    })(dispatch)
                }
                dispatch(setSeasonalsPanelOpen(false))
                dispatch(setTechnicalsPanelOpen(false))
                dispatch(setFinancialPanelOpen(false))
                dispatch(setFinancialPanelSection('overview'))
                navigate(getNavRoute(ROUTES.financials))
                onComplete?.()
                return
            }

            let route = ''
            if (security && !expressionMode) {
                const securityKey = getSecurityKey(security.exchange, security.code)
                const isFnO = isFnOSecurity(security)
                if (
                    [
                        ViewType.OPTION_CHAIN,
                        ViewType.STRATEGY_BUILDER,
                        ViewType.MULTI_STRIKES_CHART,
                        ViewType.SIMULATOR,
                    ].includes(selectedView)
                ) {
                    updatePrimarySecurityAsync({
                        key: securityKey,
                        expressionMode: false,
                        securities: [securityKey],
                        underlyingSecurityCode: getSecurityKey(
                            security.exchange,
                            isFnO ? security.underlyingSecurityCode! : security.code,
                        ),
                    })(dispatch)
                }
            }

            if (selectedView === ViewType.CHART) {
                dispatch(setFinancialPanelOpen(false))
                dispatch(setSeasonalsPanelOpen(false))
                dispatch(setTechnicalsPanelOpen(false))
                route = ROUTES.chart
            } else if (selectedView === ViewType.OPTION_CHAIN) {
                route = ROUTES.optionChain
            } else if (selectedView === ViewType.OI_OVERVIEW) {
                route = ROUTES.oiOverview
            } else if (selectedView === ViewType.OPEN_INTEREST) {
                route = ROUTES.openInterest
            } else if (selectedView === ViewType.OPEN_INTEREST_CHANGE) {
                route = ROUTES.openInterestChange
            } else if (selectedView === ViewType.MULTI_STRIKES_CHART) {
                route = ROUTES.multiStrikeChart
            } else if (selectedView === ViewType.SCALPER) {
                route = ROUTES.scalper
            } else if (selectedView === ViewType.SIMULATOR) {
                route = ROUTES.simulator
            } else if (selectedView === ViewType.STRATEGY_BUILDER) {
                route = ROUTES.strategyBuilder
            }

            if (selectedView === ViewType.SCALPER && security) {
                dispatch(setScalperState({ initSecurity: security }))
            }

            if (selectedView === ViewType.SCALPER) {
                commonActions.dispatchRightSideWidget(dispatch, WidgetListItems.POSITIONS)
            }

            if (route) {
                navigate(getNavRoute(route))
            }

            onComplete?.()
        },
        [dispatch, expressionMode, getNavRoute, navigate, onComplete, security],
    )
}

export default useSecurityViewSelect
